import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Heart, MessageCircle, Send, Bookmark, Volume2, Lock, Sparkles, X, Loader2, ShieldAlert, BookOpen } from 'lucide-react';
import { mediaUrl, thumbUrl, thumbSrcSet, buyPost } from '../api/posts';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { togglePostLike, togglePostSave, listComments, addComment, deleteComment } from '../api/social';
import { useToast } from '../hooks/use-toast';
import { Input } from './ui/input';
import { Button } from './ui/button';
import VerifiedBadge from './VerifiedBadge';
import FeedVideo from './FeedVideo';
import InsufficientBalanceDialog from './InsufficientBalanceDialog';
import { fmtCredits } from '../lib/currency';
import PostActionsMenu from './PostActionsMenu';
import SharePostDialog from './SharePostDialog';
import AudioAvatarPlayer from './AudioAvatarPlayer';
import useAppConfig from '../hooks/useAppConfig';
import RaccontoPlayer from './racconti/RaccontoPlayer';

const timeAgo = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString();
};

const Avatar = ({ src, name }) => {
  // Phase 51 · Avatars are tiny 32px displays — request a 160px WebP
  // thumb (2× for retina) instead of the full-size upload.
  const url = useMemo(() => (src ? (src.startsWith('http') ? src : `${src}`) : ''), [src]);
  return (
    <div className="rounded-full bg-gradient-to-tr from-amber-300 via-yellow-400 to-amber-600 p-[2px]">
      <div className="rounded-full bg-card p-[1.5px]">
        {url ? (
          <img
            src={thumbUrl(url, 160)}
            alt={name}
            className="h-8 w-8 rounded-full object-cover"
            loading="lazy"
            decoding="async"
            width="32"
            height="32"
          />
        ) : (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-muted-foreground">
            {(name || '?').charAt(0).toUpperCase()}
          </div>
        )}
      </div>
    </div>
  );
};

const LockedOverlay = ({ post, onUnlock, busy }) => (
  <div className="relative aspect-square w-full overflow-hidden" data-testid="post-paywall">
    <div className="absolute inset-0 bg-gradient-to-br from-amber-900/30 via-neutral-800 to-black" style={{ filter: 'blur(24px)' }} />
    <div className="absolute inset-0 bg-black/40" />
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-5 text-foreground">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10 backdrop-blur-md ring-1 ring-amber-300/40">
        <Lock className="h-6 w-6 gold-text" strokeWidth={1.8} />
      </div>
      <div className="text-center">
        <p className="text-[10px] uppercase tracking-widest gold-text/80">Contenuto a pagamento</p>
        {post.title && <p className="mt-1 text-base font-bold" data-testid="paywall-title">{post.title}</p>}
        <p className="mt-0.5 text-sm font-semibold">
          {post.media_type === 'text'
            ? 'Leggi con crediti'
            : post.media_type === 'audio'
              ? 'Ascolta con crediti'
              : 'Accedi con crediti'}
        </p>
      </div>

      {/* Phase 18.2 — public teaser shown above the unlock button so buyers
          know what they're paying for. Falls back to the caption if no
          teaser was provided (legacy posts). */}
      {(post.preview_description || post.caption) && (
        <div
          className="w-full max-w-sm rounded-lg bg-black/40 px-3 py-2 text-center text-xs leading-snug text-foreground/95 backdrop-blur-sm ring-1 ring-amber-500/20"
          data-testid="paywall-preview-description"
        >
          {post.preview_description ? (
            <span className="whitespace-pre-wrap break-words">{post.preview_description}</span>
          ) : (
            <>
              <span className="font-semibold gold-text">{post.author?.username}</span>{' '}
              <span className="whitespace-pre-wrap break-words line-clamp-4">{post.caption}</span>
            </>
          )}
        </div>
      )}

      <button
        onClick={onUnlock}
        disabled={busy}
        className="flex items-center gap-2 rounded-full gold-gradient px-5 py-2.5 text-sm font-semibold shadow-lg shadow-amber-900/30 disabled:opacity-60"
        data-testid="paywall-unlock-btn"
      >
        <Sparkles className="h-4 w-4" />
        {busy ? 'Caricamento…' : `${post.media_type === 'text' ? 'Leggi' : post.media_type === 'audio' ? 'Ascolta' : 'Accedi'} per ${fmtCredits(post.price)}`}
      </button>
      <p className="text-[11px] text-foreground/60">Pagamento una tantum · accesso immediato</p>
    </div>
  </div>
);

const SmartImage = ({ src, srcSet, sizes, alt, className }) => {
  const [errored, setErrored] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  useEffect(() => {
    setErrored(false);
    setLoaded(false);
    setRetryKey((k) => k + 1);
  }, [src]);
  if (!src) return <div className={`flex items-center justify-center bg-secondary ${className}`}><Lock className="h-6 w-6 text-muted-foreground" /></div>;
  if (errored) {
    return (
      <div className={`flex items-center justify-center bg-secondary text-muted-foreground ${className}`}>
        <button
          onClick={() => { setErrored(false); setRetryKey((k) => k + 1); }}
          className="text-xs font-semibold underline"
        >Retry loading</button>
      </div>
    );
  }
  return (
    <>
      {/* Skeleton shimmer while the thumb is decoding — prevents blank card
          during scroll on slow connections. */}
      {!loaded && (
        <div
          className={`absolute inset-0 animate-pulse bg-gradient-to-br from-neutral-800 via-neutral-900 to-neutral-800 ${className}`}
          aria-hidden="true"
        />
      )}
      <img
        key={retryKey}
        src={src}
        srcSet={srcSet}
        sizes={sizes}
        alt={alt}
        className={`${className} ${loaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-200`}
        loading="lazy"
        decoding="async"
        // Phase 51 · Give the browser priority hints to schedule work
        // efficiently for feed images (medium priority — hero images
        // in landing pages would be `fetchPriority="high"`).
        fetchPriority="auto"
        onLoad={() => setLoaded(true)}
        onError={() => setErrored(true)}
      />
    </>
  );
};

const MediaRenderer = ({ post }) => {
  const rawPath = post.media_url;
  const url = mediaUrl(rawPath);
  // Phase 51 · Feed images: request a lightweight thumbnail as the
  // default `src` (800px covers most mobile cases at 1x/2x) plus a
  // full `srcset` so the browser can pick a better bucket at 3x DPR.
  const defaultSrc = useMemo(() => thumbUrl(rawPath, 800), [rawPath]);
  const srcSet = useMemo(() => thumbSrcSet(rawPath), [rawPath]);
  const w = Number(post.media_width || 0);
  const h = Number(post.media_height || 0);
  // Compute aspect-ratio style for images and the audio block. Videos use
  // FeedVideo which handles aspect ratio internally.
  const aspectStyle = (() => {
    if (w > 0 && h > 0) {
      const ratio = Math.max(0.4, Math.min(1.9, w / h));
      return { aspectRatio: String(ratio) };
    }
    return { aspectRatio: '1 / 1' };
  })();

  if (post.media_type === 'image') {
    return (
      <div className="relative w-full overflow-hidden bg-secondary" style={aspectStyle}>
        <SmartImage
          src={defaultSrc}
          srcSet={srcSet}
          sizes="(max-width: 640px) 100vw, 520px"
          alt="post"
          className="h-full w-full object-cover"
        />
      </div>
    );
  }
  if (post.media_type === 'video') {
    // Phase 84 — while the pipeline runs (or the AI held the video) the
    // author sees a status card instead of the player.
    if (post.approval_status && post.approval_status !== 'approved') {
      const st = post.approval_status;
      const label = st === 'processing'
        ? 'Video in elaborazione…'
        : st === 'processing_failed'
          ? 'Elaborazione non riuscita'
          : st === 'pending'
            ? 'Video in revisione'
            : 'Video bloccato dalla moderazione';
      const sub = st === 'processing'
        ? 'Ottimizzazione e verifica in corso. Visibile solo a te fino alla pubblicazione.'
        : st === 'processing_failed'
          ? 'Qualcosa è andato storto durante l\'elaborazione. Ricarica il video per pubblicarlo.'
          : st === 'pending'
            ? 'Un moderatore lo controllerà a breve. Nel frattempo è visibile solo a te.'
            : 'Contatta il supporto se pensi si tratti di un errore.';
      return (
        <div
          className="flex aspect-video w-full flex-col items-center justify-center gap-2 bg-neutral-900 px-6 text-center"
          data-testid={`video-status-${st}-${post.id}`}
        >
          {st === 'processing'
            ? <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
            : <ShieldAlert className="h-8 w-8 text-amber-400" />}
          <p className="text-sm font-semibold text-white">{label}</p>
          <p className="text-xs text-neutral-400">{sub}</p>
        </div>
      );
    }
    return (
      <FeedVideo
        src={url}
        postId={post.id}
        width={w}
        height={h}
        // Phase 51.2 · Pipeline poster: the feed shows a lightweight WebP
        // cover — the video itself is only fetched once it hits the viewport.
        poster={post.poster_url ? thumbUrl(post.poster_url, 640) : undefined}
      />
    );
  }
  if (post.media_type === 'audio') {
    // Phase F7 — same RaccontoPlayer as the Vetrina (no second system).
    return (
      <div className="flex w-full flex-col gap-4 bg-gradient-to-br from-neutral-800 to-neutral-900 px-4 py-6 sm:px-6">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-card shadow-md gold-ring">
            <Volume2 className="h-6 w-6 gold-text" strokeWidth={1.6} />
          </div>
          <div className="min-w-0">
            {post.title && (
              <p className="line-clamp-2 text-base font-bold text-white" data-testid={`post-audio-title-${post.id}`}>{post.title}</p>
            )}
            {post.author?.username && (
              <p className="text-[11px] text-neutral-400">@{post.author.username}</p>
            )}
          </div>
        </div>
        <RaccontoPlayer post={post} />
      </div>
    );
  }
  if (post.media_type === 'text') {
    // Phase F6 — Racconto Testuale: clean reading layout. body_text arrives
    // ONLY when the viewer has access (server-side gating).
    return (
      <div className="bg-card px-4 py-5" data-testid={`post-text-body-${post.id}`}>
        <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider gold-text">
          <BookOpen className="h-3.5 w-3.5" /> Racconto
        </div>
        {post.title && <h2 className="mb-2 text-lg font-bold">{post.title}</h2>}
        <p className="whitespace-pre-wrap break-words text-[15px] leading-relaxed text-foreground/95">{post.body_text}</p>
      </div>
    );
  }
  return null;
};

const CommentsDrawer = ({ post, open, onClose }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    listComments(post.id).then((c) => { setComments(c); setLoading(false); }).catch(() => setLoading(false));
  }, [open, post.id]);

  if (!open) return null;

  const submit = async () => {
    if (!text.trim()) return;
    setSubmitting(true);
    try {
      const c = await addComment(post.id, text);
      setComments((cs) => [c, ...cs]);
      setText('');
    } catch (e) {
      toast({ title: 'Could not add comment', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (cid) => {
    try {
      await deleteComment(post.id, cid);
      setComments((cs) => cs.filter((c) => c.id !== cid));
    } catch { /* ignore */ }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 sm:items-center">
      <div className="flex h-[80vh] w-full max-w-md flex-col rounded-t-2xl bg-card sm:rounded-2xl">
        <header className="flex items-center justify-between border-b border-border px-4 py-3">
          <h3 className="text-sm font-semibold">Comments</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
        </header>
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {loading ? (
            <div className="flex justify-center py-10">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-border border-t-amber-400" />
            </div>
          ) : comments.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">No comments yet. Be the first.</p>
          ) : (
            <ul className="flex flex-col gap-3">
              {comments.map((c) => (
                <li key={c.id} className="flex items-start gap-2 text-sm">
                  <Avatar src={c.avatar_url} name={c.username} />
                  <div className="flex-1">
                    <p>
                      <span className="font-semibold">{c.username}</span>{' '}
                      <span className="text-foreground/90">{c.text}</span>
                    </p>
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{timeAgo(c.created_at)} ago</p>
                  </div>
                  {(user?.id === c.user_id || user?.id === post.user_id) && (
                    <button onClick={() => remove(c.id)} className="text-muted-foreground hover:text-red-500">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex items-center gap-2 border-t border-border px-3 py-2">
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            placeholder="Add a comment..."
            className="h-9 bg-background"
            disabled={submitting}
          />
          <Button onClick={submit} disabled={submitting || !text.trim()} className="h-9 gold-gradient">Post</Button>
        </div>
      </div>
    </div>
  );
};

const PostCard = ({ post, onUpdated, onHidden }) => {
  // Phase F2 · Voice-First: hide Like UI when the People Feed is ON.
  const { peopleFeedEnabled } = useAppConfig();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [liked, setLiked] = useState(post.liked_by_me || false);
  const [saved, setSaved] = useState(post.saved_by_me || false);
  const [likes, setLikes] = useState(post.likes_count || 0);
  const [comments, setComments] = useState(post.comments_count || 0);
  const [unlocking, setUnlocking] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [insufficient, setInsufficient] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  // Phase F6 — stable idempotency key per unlock intent (survives retries).
  const unlockRef = useRef('');

  useEffect(() => {
    setLiked(!!post.liked_by_me);
    setSaved(!!post.saved_by_me);
    setLikes(post.likes_count || 0);
    setComments(post.comments_count || 0);
  }, [post.liked_by_me, post.saved_by_me, post.likes_count, post.comments_count]);

  const isOwner = user && user.id === post.user_id;
  const locked = post.is_paid && !post.has_access && !isOwner;

  const doLike = async () => {
    if (!user) {
      toast({ title: 'Accedi per continuare', description: 'Registrati o accedi per interagire.' });
      navigate('/login');
      return;
    }
    // optimistic
    setLiked((v) => !v);
    setLikes((n) => (liked ? n - 1 : n + 1));
    try {
      const res = await togglePostLike(post.id);
      setLiked(res.liked);
      setLikes(res.likes_count);
    } catch {
      // revert
      setLiked((v) => !v);
      toast({ title: 'Could not like', variant: 'destructive' });
    }
  };

  const doSave = async () => {
    if (!user) {
      toast({ title: 'Accedi per salvare', description: 'Registrati o accedi per aggiungere ai preferiti.' });
      navigate('/login');
      return;
    }
    setSaved((v) => !v);
    try {
      const res = await togglePostSave(post.id);
      setSaved(res.saved);
    } catch {
      setSaved((v) => !v);
      toast({ title: 'Could not save', variant: 'destructive' });
    }
  };

  const handleUnlock = async () => {
    if (unlocking) return;
    if (!unlockRef.current) {
      unlockRef.current = (window.crypto?.randomUUID?.() || `${post.id}_${Date.now()}_${Math.random().toString(36).slice(2)}`);
    }
    setUnlocking(true);
    try {
      const res = await buyPost(post.id, unlockRef.current);
      toast({
        title: 'Acquisto completato',
        description: `Hai pagato ${fmtCredits(res?.purchase?.amount || post.price)} dal Wallet.`,
      });
      // Tell parents to refresh.
      try { window.dispatchEvent(new CustomEvent('nymia:data-changed')); } catch (_) { /* noop */ }
      // Soft refresh of the post in place — most callers will re-fetch via the
      // global event; we just unlock the visible overlay optimistically.
      post.has_access = true;
      unlockRef.current = '';
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (err.response?.status === 402 && detail.startsWith('insufficient_balance')) {
        setInsufficient(true);
      } else {
        toast({
          title: 'Acquisto non riuscito',
          description: detail.split('|')[1] || detail || 'Riprova più tardi.',
          variant: 'destructive',
        });
      }
    } finally {
      setUnlocking(false);
    }
  };

  const goToUser = () => post.author?.username && navigate(`/u/${post.author.username}`);

  return (
    <article className="mb-6 border-b border-border bg-card md:rounded-lg md:border md:border-border">
      <header className="flex items-center justify-between px-3 py-2.5">
        <div className="flex items-center gap-3">
          <button onClick={goToUser} className="flex items-center gap-3">
            <span className="relative inline-block">
              <Avatar src={post.author?.avatar_url} name={post.author?.username} />
              {post.author?.audio_avatar_url && (
                <AudioAvatarPlayer
                  src={post.author.audio_avatar_url}
                  size="sm"
                  className="absolute -bottom-0.5 -right-0.5"
                />
              )}
            </span>
            <div className="flex flex-col leading-tight text-left">
              <span className="inline-flex items-center gap-1 text-sm font-semibold hover:underline">
                {post.author?.username}
                {post.author?.is_verified_creator && <VerifiedBadge size={13} />}
              </span>
              {post.location && <span className="text-xs text-muted-foreground">{post.location}</span>}
            </div>
          </button>
          {post.is_paid && (
            <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide gold-text">
              <Sparkles className="h-3 w-3" /> Paid
            </span>
          )}
        </div>
        <PostActionsMenu post={post} onHidden={onHidden} onUpdated={onUpdated} onDeleted={onHidden} />
      </header>

      {locked ? <LockedOverlay post={post} onUnlock={handleUnlock} busy={unlocking} /> : <MediaRenderer post={post} />}

      <InsufficientBalanceDialog
        open={insufficient}
        onOpenChange={setInsufficient}
        balance={Number(user?.wallet_balance || 0)}
        required={Number(post.price || 0)}
        context="post"
      />

      <div className="flex items-center justify-between px-3 pt-3">
        <div className="flex items-center gap-3">
          {!peopleFeedEnabled && (
            <button onClick={doLike} aria-label="Like">
              <Heart
                className={`h-7 w-7 transition-transform active:scale-90 ${liked ? 'fill-red-500 text-red-500' : 'text-foreground'}`}
                strokeWidth={1.7}
              />
            </button>
          )}
          <button onClick={() => setCommentsOpen(true)} aria-label="Comment" data-testid={`post-comment-btn-${post.id}`}>
            <MessageCircle className="h-7 w-7" strokeWidth={1.7} />
          </button>
          <button aria-label="Share" onClick={() => setShareOpen(true)} data-testid={`post-share-btn-${post.id}`}>
            <Send className="h-7 w-7" strokeWidth={1.7} />
          </button>
        </div>
        <button onClick={doSave} aria-label="Save" data-testid={`post-save-btn-${post.id}`}>
          <Bookmark className={`h-7 w-7 ${saved ? 'fill-amber-400 gold-text' : ''}`} strokeWidth={1.7} />
        </button>
      </div>

      <div className="px-3 pt-2 text-sm">
        {!peopleFeedEnabled && (
          <p className="font-semibold">{likes.toLocaleString()} {likes === 1 ? 'like' : 'likes'}</p>
        )}
        {post.caption && (
          <p className="mt-1 whitespace-pre-wrap break-words">
            <span className="font-semibold">{post.author?.username}</span> <span>{post.caption}</span>
          </p>
        )}
        {comments > 0 && (
          <button onClick={() => setCommentsOpen(true)} className="mt-1 text-sm text-muted-foreground">
            View all {comments} comments
          </button>
        )}
        <p className="mt-1 pb-3 text-[10px] uppercase tracking-wide text-muted-foreground">
          {timeAgo(post.created_at)} ago
          {post.is_paid && post.has_access && !isOwner ? ' · Unlocked' : ''}
        </p>
      </div>

      <CommentsDrawer post={post} open={commentsOpen} onClose={() => { setCommentsOpen(false); }} />
      <SharePostDialog open={shareOpen} onOpenChange={setShareOpen} post={post} />
    </article>
  );
};

/**
 * Phase 49 · Feed performance — memoize by post id + mutable fields
 * that actually affect the visual output. Prevents the whole feed
 * from re-rendering when a sibling card's like/comment count changes.
 * Phase 51.1 · BUGFIX — the comparator checked fields that don't exist
 * on the API payload (`likes`, `viewer_liked`, `viewer_saved`), so a
 * refreshed feed could leave cards with stale like/save state. Aligned
 * to the real PostOut field names.
 */
const arePropsEqual = (prev, next) => {
  const a = prev.post || {};
  const b = next.post || {};
  return (
    a.id === b.id &&
    a.media_url === b.media_url &&
    a.poster_url === b.poster_url &&
    a.caption === b.caption &&
    a.likes_count === b.likes_count &&
    a.comments_count === b.comments_count &&
    a.has_access === b.has_access &&
    a.liked_by_me === b.liked_by_me &&
    a.saved_by_me === b.saved_by_me
  );
};

export default React.memo(PostCard, arePropsEqual);
